import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, StatusBadge } from "@/components/ui-helpers";
import { Plus, X, Package, Wrench, HardDrive, Save, Pencil, Trash2, Search, Globe, Download, Upload, AlertTriangle, CheckCircle2, Loader2, Lock } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import { ActiveStatusCell, ActiveStatusFilter, matchActiveFilter } from "@/components/ActiveStatusCell";

const TYPE_META = {
  Service:   { icon: Wrench,   color: "bg-sky-100 text-sky-800",       hint: "No stock or asset; purely a service transaction" },
  Inventory: { icon: Package,  color: "bg-emerald-100 text-emerald-800", hint: "On Purchase Invoice approval → auto-stocks in" },
  Asset:     { icon: HardDrive,color: "bg-violet-100 text-violet-800",  hint: "On Purchase Invoice approval → auto-creates assets ready to assign" },
};

const blank = () => ({
  item_code: "", name: "", item_type: "Inventory",
  type: "", rc_line_item_no: "", arc_number: "", order_number: "", order_month: "", client_server: "", brand: "", warranty: "",
  category: "General", group_id: "", group_name: "",
  unit: "NOS", hsn_sac_id: "", hsn_code: "", sac_code: "",
  purchase_rate: 0, purchase_discount_type: "none", purchase_discount_value: 0,
  sales_rate: 0, management_fee_type: "none", management_fee_value: 0,
  gst_rate: 18, description: "",
  reorder_level: 0, min_stock_level: 0, reorder_qty: 0,
  asset_category: "", depreciation_rate: 0, useful_life_years: 0,
  is_active: true,
});

function calcEffective(rate, mode, value, sign) {
  const r = Number(rate || 0); const v = Number(value || 0);
  if (mode === "percent") return r + sign * (r * v / 100);
  if (mode === "amount") return r + sign * v;
  return r;
}

export default function ItemsPage() {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterGroup, setFilterGroup] = useState("All");
  const [filterActive, setFilterActive] = useState("active");
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [updateResult, setUpdateResult] = useState(null);
  const [updating, setUpdating] = useState(false);
  const updateFileRef = useRef(null);

  const { items, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/items");
  const openFormTab = useFormTab();

  // Pull EVERY item (slim projection) for the Group filter dropdown counts,
  // because `items` above is only the current 200-row page. Without this, a
  // tenant with 6,651 items would only see groups from the visible page.
  const [allItemsForGroups, setAllItemsForGroups] = useState([]);
  const reloadAllForGroups = useCallback(() => {
    api.get("/items/_pickable").then(r => setAllItemsForGroups(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { reloadAllForGroups(); }, [total, reloadAllForGroups]);

  // Combined refresher invoked after every save/delete. Refreshes the
  // paginated `/items` view AND the slim `/items/_pickable` cache so that
  // mutations which don't change `total` (e.g. toggling is_active, renaming
  // a single item) are still reflected in the list without a manual reload.
  // This was the root cause of "deactivate item → saved but still shows as
  // Active" — the pickable cache was stale because total stayed unchanged.
  const load = useCallback(() => {
    reload();
    reloadAllForGroups();
  }, [reload, reloadAllForGroups]);

  function openNew() {
    openFormTab({
      path: "/items/new",
      label: "New Item",
      icon: Plus,
      parent: "/items",
      onSaved: load,
      render: ({ close }) => <ItemForm record={blank()} onClose={close} />,
    });
  }
  function openEdit(it) {
    // Always re-fetch the FULL item from /items/{id} before rendering the
    // edit form. The row we receive may have come from the slim `/items/_pickable`
    // projection (when a Group filter or search query is active) which can
    // omit free-text fields like `arc_number`, `order_number`, `client_server`.
    // Prefilling those as `undefined` and saving would silently wipe them from
    // the DB — a regression we explicitly want to prevent here.
    openFormTab({
      path: `/items/edit/${it.id}`,
      label: `Edit · ${it.item_code || it.name || "Item"}`,
      icon: Pencil,
      parent: "/items",
      onSaved: load,
      render: ({ close }) => <ItemEditFormFetched id={it.id} fallback={it} onClose={close} />,
    });
  }
  async function del(id, name) {
    if (!window.confirm(`Delete item "${name}"?`)) return;
    await api.delete(`/items/${id}`);
    toast.success("Deleted");
    load();
  }

  async function downloadTemplate() {
    try {
      const res = await api.get("/items-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "items_master_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error("Could not download template");
    }
  }

  async function handleBulkUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/items-bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(data);
      if (data.created > 0) toast.success(`${data.created} item${data.created === 1 ? "" : "s"} created${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      else if (data.skipped > 0) toast.error(`No items created · ${data.skipped} row${data.skipped === 1 ? "" : "s"} skipped — see details`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally { setUploading(false); }
  }

  async function downloadUpdateTemplate() {
    try {
      const res = await api.get("/items/bulk-update-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "items_bulk_update_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Update template downloaded");
    } catch (e) {
      toast.error("Could not download update template");
    }
  }

  async function handleBulkUpdate(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUpdating(true);
    setUpdateResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/items/bulk-update", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpdateResult(data);
      if (data.updated > 0) toast.success(`${data.updated} item${data.updated === 1 ? "" : "s"} updated${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      else if (data.skipped > 0) toast.error(`No items updated · ${data.skipped} row${data.skipped === 1 ? "" : "s"} skipped — see details`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally { setUpdating(false); }
  }

  // Derive unique group labels (and counts) from the FULL items list (not just
  // the visible page). We bucket by the same logic the voucher pickers use —
  // id when set, else lowercased name.
  const groupBuckets = (() => {
    const source = allItemsForGroups.length ? allItemsForGroups : items;
    const map = new Map();   // key → { label, count }
    let unassigned = 0;
    for (const it of source) {
      const gname = (it.group_name || "").trim();
      const gid = (it.group_id || "").trim();
      if (!gname && !gid) { unassigned += 1; continue; }
      const key = gid ? `id:${gid}` : `name:${gname.toLowerCase()}`;
      const entry = map.get(key) || { key, label: gname || gid, count: 0 };
      entry.count += 1;
      map.set(key, entry);
    }
    const arr = [...map.values()].sort((a, b) => b.count - a.count);
    if (unassigned > 0) arr.push({ key: "__unassigned__", label: "(no group)", count: unassigned, _unassigned: true });
    return arr;
  })();

  // When a Group filter OR a search query is active, work from the FULL
  // items list — otherwise duplicate item_codes that live on later pages get
  // missed (e.g. same code "3039648" registered under two different groups
  // would only return one row because the regular paginated `/items` endpoint
  // sends 200 rows at a time and the search runs client-side).
  // When neither filter is active, use the paginated source so the table
  // stays fast for huge tenants (6000+ items).
  const useAllSource = filterGroup !== "All" || (search || "").trim().length > 0;
  const baseRows = useAllSource ? allItemsForGroups : items;

  const filtered = baseRows.filter(i => {
    if (filterType !== "All" && i.item_type !== filterType) return false;
    if (!matchActiveFilter(i, filterActive)) return false;
    if (filterGroup !== "All") {
      const gname = (i.group_name || "").trim();
      const gid = (i.group_id || "").trim();
      if (filterGroup === "__unassigned__") {
        if (gname || gid) return false;
      } else {
        const itemKey = gid ? `id:${gid}` : `name:${gname.toLowerCase()}`;
        if (itemKey !== filterGroup) return false;
      }
    }
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (i.item_code || "").toLowerCase().includes(s) ||
           (i.name || "").toLowerCase().includes(s) ||
           (i.category || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="items-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Master · Global"
        title="Items"
        subtitle="Service / Inventory / Asset items. This master is shared across all branches — any change reflects everywhere. Default purchase & sales rates auto-fill into every PI / SI line."
        onRefresh={load}
        refreshing={loading}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
              data-testid="bulk-upload-input" onChange={handleBulkUpload} />
            <input ref={updateFileRef} type="file" accept=".xlsx,.xls" className="hidden"
              data-testid="bulk-update-input" onChange={handleBulkUpdate} />
            <button data-testid="download-template-btn" onClick={downloadTemplate}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Download size={14} /> Template
            </button>
            <button data-testid="bulk-upload-btn" disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="bg-white border border-[#F97316] hover:bg-orange-50 text-[#F97316] px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
              <Upload size={14} /> {uploading ? "Uploading…" : "Bulk Upload"}
            </button>
            <button data-testid="download-update-template-btn" onClick={downloadUpdateTemplate}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Download size={14} /> Update Template
            </button>
            <button data-testid="bulk-update-btn" disabled={updating}
              onClick={() => updateFileRef.current?.click()}
              className="bg-white border border-sky-500 hover:bg-sky-50 text-sky-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
              <Upload size={14} /> {updating ? "Updating…" : "Bulk Update"}
            </button>
            <button data-testid="add-item-btn" onClick={openNew}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> Add Item
            </button>
          </div>
        }
      />

      <div className="mb-3 flex items-center gap-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 px-3 py-2" data-testid="items-global-banner">
        <Globe size={13} /> <span className="font-bold uppercase tracking-wider">Global Master</span>
        <span className="text-slate-600 normal-case">— visible &amp; usable across every branch. Edits propagate everywhere instantly. Bulk-upload up to 3000 rows per file.</span>
      </div>

      {uploadResult && (
        <div className="mb-4 border border-slate-200 bg-white px-4 py-3" data-testid="bulk-upload-result">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                <CheckCircle2 size={12} /> {uploadResult.created} created
              </span>
              {uploadResult.skipped > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5">
                  <AlertTriangle size={12} /> {uploadResult.skipped} skipped
                </span>
              )}
              <span className="text-xs text-slate-500">Total rows: <span className="font-bold tabular">{uploadResult.total_rows}</span> / max {uploadResult.max_rows}</span>
            </div>
            <button onClick={() => setUploadResult(null)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
          </div>
          {uploadResult.errors?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-[#F97316] font-bold">
                Show {uploadResult.error_count} skipped row{uploadResult.error_count === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 pl-4 list-disc space-y-0.5">
                {uploadResult.errors.map((er, i) => (
                  <li key={i} className="text-amber-900"><span className="font-mono text-slate-500">Row {er.row}:</span> {er.error}</li>
                ))}
                {uploadResult.error_count > uploadResult.errors.length && (
                  <li className="text-slate-400 italic">…and {uploadResult.error_count - uploadResult.errors.length} more (cap reached)</li>
                )}
              </ul>
            </details>
          )}
        </div>
      )}

      {updateResult && (
        <div className="mb-4 border border-slate-200 bg-white px-4 py-3" data-testid="bulk-update-result">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-sky-800 bg-sky-50 border border-sky-200 px-2 py-0.5">
                <CheckCircle2 size={12} /> {updateResult.updated} updated
              </span>
              {updateResult.skipped > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5">
                  <AlertTriangle size={12} /> {updateResult.skipped} skipped
                </span>
              )}
              <span className="text-xs text-slate-500">Total rows: <span className="font-bold tabular">{updateResult.total}</span></span>
            </div>
            <button onClick={() => setUpdateResult(null)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
          </div>
          {updateResult.errors?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-sky-700 font-bold">
                Show {updateResult.errors.length} row{updateResult.errors.length === 1 ? "" : "s"} with notes
              </summary>
              <ul className="mt-2 pl-4 list-disc space-y-0.5">
                {updateResult.errors.map((er, i) => (
                  <li key={i} className="text-amber-900"><span className="font-mono text-slate-500">Row {er.row}:</span> {er.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Search by code, name, category…"
                 className="w-full pl-9 pr-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none"
                 data-testid="item-search" />
        </div>
        <div className="flex gap-1">
          {["All", "Service", "Inventory", "Asset"].map(t => (
            <button key={t} onClick={() => setFilterType(t)}
                    data-testid={`filter-${t.toLowerCase()}`}
                    className={`px-3 py-2 text-[11px] uppercase tracking-wider font-bold border ${filterType === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-[#F97316]"}`}>
              {t}
            </button>
          ))}
        </div>
        <ActiveStatusFilter value={filterActive} onChange={setFilterActive} testid="items-filter-active" />
        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
                data-testid="items-filter-group"
                className="px-3 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 bg-white text-slate-700 focus:border-[#F97316] focus:outline-none">
          <option value="All">All Groups ({allItemsForGroups.length || total || items.length})</option>
          {groupBuckets.map((g, idx) => (
            <option key={g.key || idx} value={g.key || (g._unassigned ? "__unassigned__" : `${g.label.toLowerCase()}`)}>
              {g._unassigned ? "⚠ (no group)" : g.label} ({g.count})
            </option>
          ))}
        </select>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full" data-testid="items-table">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              {["Code", "Name", "Item Variety", "RC Line No.", "Group", "Item Type", "Unit", "Purchase Rate (Net)", "Sales Rate (Net)", "GST %", "Status", ""].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="p-10 text-center text-slate-500 text-sm">No items found. Click <strong>Add Item</strong> to start.</td></tr>
            )}
            {filtered.map(i => {
              const M = TYPE_META[i.item_type] || TYPE_META.Inventory;
              const Icon = M.icon;
              const purchaseNet = calcEffective(i.purchase_rate, i.purchase_discount_type, i.purchase_discount_value, -1);
              const salesNet = calcEffective(i.sales_rate, i.management_fee_type, i.management_fee_value, +1);
              return (
                <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="py-3 px-4 text-xs font-bold text-slate-900">{i.item_code}</td>
                  <td className="py-3 px-4 text-sm text-slate-800">{i.name}</td>
                  <td className="py-3 px-4 text-xs text-slate-700">{i.type || "—"}</td>
                  <td className="py-3 px-4 text-xs text-slate-700 tabular">{i.rc_line_item_no || "—"}</td>
                  <td className="py-3 px-4 text-xs text-slate-700">{i.group_name || "—"}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${M.color}`}>
                      <Icon size={11} /> {i.item_type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-600">{i.unit}</td>
                  <td className="py-3 px-4 text-sm tabular text-slate-800">
                    <div>{formatINR(purchaseNet)}</div>
                    {(i.purchase_discount_type || "none") !== "none" && (
                      <div className="text-[10px] text-slate-400">
                        base {formatINR(i.purchase_rate)} − {i.purchase_discount_type === "percent" ? `${i.purchase_discount_value}%` : formatINR(i.purchase_discount_value)}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm tabular text-[#F97316] font-bold">
                    <div>{formatINR(salesNet)}</div>
                    {(i.management_fee_type || "none") !== "none" && (
                      <div className="text-[10px] text-slate-400">
                        base {formatINR(i.sales_rate)} + mgmt {i.management_fee_type === "percent" ? `${i.management_fee_value}%` : formatINR(i.management_fee_value)}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-700">{i.gst_rate}%</td>
                  <td className="py-3 px-4">
                    <ActiveStatusCell row={i} resource="items" onChanged={load} />
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`edit-${i.id}`} onClick={() => openEdit(i)} className="text-slate-500 hover:text-[#F97316] p-1"><Pencil size={14} /></button>
                      <button data-testid={`del-${i.id}`} onClick={() => del(i.id, i.name)} className="text-slate-500 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
      </Card>
    </div>
  );
}

function ItemEditFormFetched({ id, fallback, onClose }) {
  // Fetches the FULL item by id and feeds it to <ItemForm/> only after it
  // resolves. We blend with `blank()` so every form key is at least defined
  // (controlled-input warning) and merge the freshest server doc on top.
  // Falls back to the (possibly slim) row passed in if the fetch fails — the
  // user still gets *something* editable rather than a dead screen.
  const [record, setRecord] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/items/${id}`)
      .then((r) => { if (!cancelled) setRecord({ ...blank(), ...r.data }); })
      .catch(() => { if (!cancelled) setRecord({ ...blank(), ...(fallback || {}) }); });
    return () => { cancelled = true; };
  }, [id, fallback]);
  if (!record) {
    return (
      <div className="p-8 text-center text-slate-500 text-sm">
        <div className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading item…</div>
      </div>
    );
  }
  return <ItemForm record={record} onClose={onClose} />;
}

function ItemForm({ record, onClose }) {
  const [form, setForm] = useState(record);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [hsnSacList, setHsnSacList] = useState([]);
  const [uoms, setUoms] = useState([]);
  // Once an item is referenced in ANY transaction, its `item_type` becomes
  // immutable (Inventory ↔ Asset ↔ Service swap would corrupt downstream
  // stock / asset / service classifications). Backend enforces this on
  // PUT /items/{id}; the UI fetches /items/{id}/usage to render the lock
  // pre-emptively so users aren't surprised by a 409.
  const [typeUsage, setTypeUsage] = useState({ in_use: false, references: [] });
  const isExisting = !!record?.id;
  const isTypeLocked = isExisting && typeUsage.in_use;
  const M = TYPE_META[form.item_type] || TYPE_META.Inventory;
  const TypeIcon = M.icon;

  useEffect(() => {
    api.get("/item-groups").then(r => setGroups(r.data || []));
    api.get("/hsn-sac").then(r => setHsnSacList(r.data || []));
    api.get("/uom-master").then(r => setUoms((r.data || []).sort((a, b) => a.code.localeCompare(b.code))));
    if (record?.id) {
      api.get(`/items/${record.id}/usage`)
        .then(r => setTypeUsage(r.data || { in_use: false, references: [] }))
        .catch(() => {/* non-fatal — backend still enforces */});
    }
  }, [record?.id]);

  // Sprint 3.12 — Defensive resolver. Items imported via bulk upload
  // BEFORE the resolve-ids fix landed carry `group_name` / `hsn_code`
  // but blank `group_id` / `hsn_sac_id`. Match by name/code so the
  // Edit-Item dropdowns render the saved value instead of "— Select —"
  // even before the backfill endpoint is hit.
  useEffect(() => {
    if (!groups.length && !hsnSacList.length) return;
    let next = form;
    let changed = false;
    if (groups.length && !form.group_id && (form.group_name || "").trim()) {
      const match = groups.find(g => (g.name || "").trim().toLowerCase() === (form.group_name || "").trim().toLowerCase());
      if (match) { next = { ...next, group_id: match.id, group_name: match.name }; changed = true; }
    }
    if (hsnSacList.length && !form.hsn_sac_id) {
      const codeRaw = (form.hsn_code || form.sac_code || "").trim();
      if (codeRaw) {
        const match = hsnSacList.find(h => (h.code || "").trim().toLowerCase() === codeRaw.toLowerCase());
        if (match) { next = { ...next, hsn_sac_id: match.id }; changed = true; }
      }
    }
    if (changed) setForm(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, hsnSacList, form.group_name, form.hsn_code, form.sac_code]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function pickGroup(gid) {
    const g = groups.find(x => x.id === gid);
    setForm({ ...form, group_id: gid, group_name: g ? g.name : "" });
  }

  function pickHsnSac(hid) {
    const h = hsnSacList.find(x => x.id === hid);
    if (!h) {
      setForm({ ...form, hsn_sac_id: "", hsn_code: "", sac_code: "" });
      return;
    }
    // Auto-fill GST rate from library entry
    setForm({
      ...form,
      hsn_sac_id: hid,
      hsn_code: h.type === "HSN" ? h.code : "",
      sac_code: h.type === "SAC" ? h.code : "",
      gst_rate: Number(h.gst_rate || 18),
    });
  }

  // Filter HSN/SAC based on item type — Service items default to SAC
  const filteredHsnSac = hsnSacList.filter(h => {
    if (form.item_type === "Service") return h.type === "SAC";
    return h.type === "HSN";
  });

  const purchaseNet = calcEffective(form.purchase_rate, form.purchase_discount_type, form.purchase_discount_value, -1);
  const salesNet = calcEffective(form.sales_rate, form.management_fee_type, form.management_fee_value, +1);

  async function save(e) {
    e.preventDefault();
    if (!form.item_code || !form.name) { toast.error("Item Code & Name are required"); return; }
    setSaving(true);
    try {
      // Sync legacy `rate` field with sales_rate so old code paths still work
      const payload = { ...form, rate: Number(form.sales_rate || 0) };
      if (form.id) await api.put(`/items/${form.id}`, payload);
      else await api.post("/items", payload);
      toast.success(form.id ? "Item updated" : "Item created");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
        <div>
          <h2 className="font-display text-2xl font-bold">{form.id ? "Edit" : "New"} Item</h2>
          <div className="text-[11px] text-slate-500 mt-0.5">{M.hint}</div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="save-item-btn" form="item-form" type="submit" disabled={saving} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Save size={14} /> {saving ? "Saving…" : "Save Item"}
          </button>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={20} /></button>
        </div>
      </div>

      <form id="item-form" onSubmit={save} className="px-8 py-6 space-y-5 max-w-[1400px] mx-auto">
          {/* Type selector */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Item Type</h3>
              {isTypeLocked && (
                <div className="inline-flex items-center gap-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-300 px-2.5 py-1"
                     data-testid="item-type-locked-banner"
                     title={typeUsage.references.map(r => `${r.count} ${r.label}`).join(", ")}>
                  <Lock size={11} className="text-amber-700" />
                  <span><strong>Locked</strong> — this item is referenced in {typeUsage.references.map(r => `${r.count} ${r.label}`).join(", ")}. Changing type would corrupt history. Mark inactive and create a new master instead.</span>
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.entries(TYPE_META).map(([type, meta]) => {
                  const Icon = meta.icon;
                  const active = form.item_type === type;
                  const disabled = isTypeLocked && !active;
                  return (
                    <button key={type} type="button"
                            onClick={() => !disabled && set("item_type", type)}
                            disabled={disabled}
                            data-testid={`type-${type.toLowerCase()}`}
                            title={disabled ? "Item type is locked once the item is used in any transaction." : ""}
                            className={`p-5 text-left border-2 transition-all ${
                              active
                                ? "border-[#F97316] bg-orange-50/50 shadow-sm"
                                : disabled
                                  ? "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed"
                                  : "border-slate-200 hover:border-slate-400 bg-white"
                            }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 flex items-center justify-center ${meta.color}`}>
                          <Icon size={18} />
                        </div>
                        <div>
                          <div className="font-bold text-slate-900">{type}</div>
                          <div className="text-[11px] text-slate-500">{meta.hint}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Identity */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <TypeIcon size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Identity & Classification</h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Item Code *"><input data-testid="item-code" value={form.item_code} onChange={e => set("item_code", e.target.value)} className={inputCls} /></Field>
              <Field label="Name *"><input data-testid="item-name" value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} /></Field>
              <Field label="Item Group">
                <select value={form.group_id || ""} onChange={e => pickGroup(e.target.value)} className={inputCls} data-testid="item-group">
                  <option value="">— Select —</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.code} · {g.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <label className="flex items-center gap-2 text-sm pt-2 cursor-pointer select-none" data-testid="item-active-toggle">
                  <input type="checkbox" checked={form.is_active !== false}
                         onChange={e => set("is_active", e.target.checked)}
                         className="w-4 h-4 accent-[#F97316]" />
                  {/* Label is INTENTIONALLY static ("Active"). The hint after
                      reflects current state — but the control's noun never
                      changes, so users can't accidentally re-activate an
                      already-inactive item by unticking what they think is
                      an "Inactive" box. (Reported bug, 2026-02-20.) */}
                  <span className="font-bold text-slate-800">Active</span>
                  <span className={`text-xs ${form.is_active !== false ? "text-emerald-700" : "text-rose-700"}`}>
                    {form.is_active !== false
                      ? "— usable on transactions"
                      : "— hidden from dropdowns by default (inactive)"}
                  </span>
                </label>
              </Field>
              <Field label="Item Variety"><input data-testid="item-variety-text" value={form.type || ""} onChange={e => set("type", e.target.value)} placeholder="e.g. Brand / Sub-type / Make (free text)" className={inputCls} /></Field>
              <Field label="RC Line Item Number"><input data-testid="item-rc-line" value={form.rc_line_item_no || ""} onChange={e => set("rc_line_item_no", e.target.value)} placeholder="Rate Contract line item no." className={inputCls} /></Field>
              <Field label="ARC Number"><input data-testid="item-arc-number" value={form.arc_number || ""} onChange={e => set("arc_number", e.target.value)} placeholder="Annual Rate Contract no." className={inputCls} /></Field>
              <Field label="Order Number"><input data-testid="item-order-number" value={form.order_number || ""} onChange={e => set("order_number", e.target.value)} placeholder="e.g. PO/2026/0123 (free text)" className={inputCls} /></Field>
              <Field label="Order Month"><input data-testid="item-order-month" value={form.order_month || ""} onChange={e => set("order_month", e.target.value)} placeholder="e.g. Mar 2026 (free text)" className={inputCls} /></Field>
              <Field label="Client Server"><input data-testid="item-client-server" value={form.client_server || ""} onChange={e => set("client_server", e.target.value)} placeholder="e.g. CSpur-DC1 (free text)" className={inputCls} /></Field>
              <Field label="Brand"><input data-testid="item-brand" value={form.brand || ""} onChange={e => set("brand", e.target.value)} placeholder="e.g. Schneider / Havells / Dell" className={inputCls} /></Field>
              <Field label="Warranty"><input data-testid="item-warranty" value={form.warranty || ""} onChange={e => set("warranty", e.target.value)} placeholder="e.g. 2 Years On-site" className={inputCls} /></Field>
              <Field label="Category (free-text)"><input value={form.category || ""} onChange={e => set("category", e.target.value)} className={inputCls} /></Field>
              <Field label="UOM (GST-compliant)">
                <select value={form.unit} onChange={e => set("unit", e.target.value)} className={inputCls} data-testid="item-unit">
                  {uoms.map(u => <option key={u.code} value={u.code}>{u.code} · {u.name}</option>)}
                </select>
              </Field>
              <Field label={form.item_type === "Service" ? "SAC Code (from library)" : "HSN Code (from library)"}>
                <select value={form.hsn_sac_id || ""} onChange={e => pickHsnSac(e.target.value)} className={inputCls} data-testid="item-hsn-sac">
                  {/* Sprint 3.12.1 — when the item has a legacy hsn_code
                      that doesn't match any HSN master row, show the raw
                      code as the placeholder option text so Edit-Item
                      reflects the data instead of an empty "— Select —".
                      Operator can still pick a real library row from
                      the dropdown. */}
                  {(form.hsn_code || form.sac_code) && !form.hsn_sac_id ? (
                    <option value="">{form.hsn_code || form.sac_code} · (legacy — pick from library to standardise)</option>
                  ) : (
                    <option value="">— Select —</option>
                  )}
                  {filteredHsnSac.map(h => <option key={h.id} value={h.id}>{h.code} · {h.description.slice(0, 40)} ({h.gst_rate}%)</option>)}
                </select>
              </Field>
              <Field label="GST Rate (%)">
                <input type="number" step="0.01" value={form.gst_rate} onChange={e => set("gst_rate", Number(e.target.value))}
                       className={inputCls + " bg-slate-50"}
                       title="Auto-filled from HSN/SAC library — edit if needed" />
              </Field>
              <div className="md:col-span-3">
                <Field label="Description"><textarea rows={2} value={form.description || ""} onChange={e => set("description", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>
          </Card>

          {/* Purchase Rate + Discount */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Purchase Rate & Discount</h3>
              <div className="text-[11px] text-slate-500">Auto-fills into every Purchase Invoice line</div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Base Purchase Rate (₹)"><input type="number" step="0.01" value={form.purchase_rate} onChange={e => set("purchase_rate", Number(e.target.value))} className={inputCls} data-testid="purchase-rate" /></Field>
              <Field label="Discount Type">
                <select value={form.purchase_discount_type} onChange={e => set("purchase_discount_type", e.target.value)} className={inputCls} data-testid="discount-type">
                  <option value="none">None</option>
                  <option value="percent">Percent (%)</option>
                  <option value="amount">Fixed Amount (₹)</option>
                </select>
              </Field>
              <Field label={form.purchase_discount_type === "percent" ? "Discount (%)" : "Discount (₹)"}>
                <input type="number" step="0.01" value={form.purchase_discount_value} onChange={e => set("purchase_discount_value", Number(e.target.value))}
                       disabled={form.purchase_discount_type === "none"}
                       className={`${inputCls} ${form.purchase_discount_type === "none" ? "bg-slate-50 text-slate-400" : ""}`} data-testid="discount-value" />
              </Field>
              <Field label="Effective Purchase Rate">
                <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-200 font-bold text-emerald-800 tabular" data-testid="purchase-net">
                  {formatINR(purchaseNet)}
                </div>
              </Field>
            </div>
          </Card>

          {/* Sales Rate + Management Fee */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Sales Rate & Management Fee</h3>
              <div className="text-[11px] text-slate-500">Auto-fills into every Sales Invoice line</div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Base Sales Rate (₹)"><input type="number" step="0.01" value={form.sales_rate} onChange={e => set("sales_rate", Number(e.target.value))} className={inputCls} data-testid="sales-rate" /></Field>
              <Field label="Management Fee Type">
                <select value={form.management_fee_type} onChange={e => set("management_fee_type", e.target.value)} className={inputCls} data-testid="mgmt-type">
                  <option value="none">None</option>
                  <option value="percent">Percent (%)</option>
                  <option value="amount">Fixed Amount (₹)</option>
                </select>
              </Field>
              <Field label={form.management_fee_type === "percent" ? "Management Fee (%)" : "Management Fee (₹)"}>
                <input type="number" step="0.01" value={form.management_fee_value} onChange={e => set("management_fee_value", Number(e.target.value))}
                       disabled={form.management_fee_type === "none"}
                       className={`${inputCls} ${form.management_fee_type === "none" ? "bg-slate-50 text-slate-400" : ""}`} data-testid="mgmt-value" />
              </Field>
              <Field label="Effective Sales Rate">
                <div className="px-3 py-2.5 bg-orange-50 border border-orange-200 font-bold text-[#F97316] tabular" data-testid="sales-net">
                  {formatINR(salesNet)}
                </div>
              </Field>
            </div>
          </Card>

          {/* Type-specific */}
          {form.item_type === "Inventory" && (
            <Card>
              <div className="px-5 py-4 border-b border-slate-100">
                <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Inventory Tracking</h3>
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Min Stock Level"><input type="number" step="0.01" value={form.min_stock_level} onChange={e => set("min_stock_level", Number(e.target.value))} className={inputCls} /></Field>
                <Field label="Reorder Level"><input type="number" step="0.01" value={form.reorder_level} onChange={e => set("reorder_level", Number(e.target.value))} className={inputCls} /></Field>
                <Field label="Reorder Qty"><input type="number" step="0.01" value={form.reorder_qty} onChange={e => set("reorder_qty", Number(e.target.value))} className={inputCls} /></Field>
              </div>
            </Card>
          )}

          {form.item_type === "Asset" && (
            <Card>
              <div className="px-5 py-4 border-b border-slate-100">
                <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Asset Configuration</h3>
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Asset Category"><input value={form.asset_category || ""} onChange={e => set("asset_category", e.target.value)} placeholder="e.g. IT Equipment" className={inputCls} /></Field>
                <Field label="Depreciation Rate (% / year)"><input type="number" step="0.01" value={form.depreciation_rate} onChange={e => set("depreciation_rate", Number(e.target.value))} className={inputCls} /></Field>
                <Field label="Useful Life (years)"><input type="number" step="0.01" value={form.useful_life_years} onChange={e => set("useful_life_years", Number(e.target.value))} className={inputCls} /></Field>
                <div className="md:col-span-3 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-3 py-2.5">
                  When a Purchase Invoice with this item is <strong>Approved</strong>, one Asset record per unit qty is auto-created. Codes follow <code className="bg-white px-1 py-0.5 border">AST/{form.item_code || "ITEMCODE"}-001</code> (prefix configurable in <em>Settings → Numbering Series → Asset</em>), ready to assign to a Member.
                </div>
              </div>
            </Card>
          )}

          {form.item_type === "Service" && (
            <div className="bg-sky-50 border border-sky-200 px-5 py-4 text-xs text-sky-800">
              <strong>Service item</strong> — no stock, no asset is created. Used purely for service charges on invoices.
            </div>
          )}
        </form>
    </div>
  );
}
